'use client'

import { useTranslation } from 'react-i18next'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import { TextLink } from '@/components/TextLink/TextLink'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'
import { StoryblokComponent } from '@storyblok/react'
import { type SbBlokData } from '@storyblok/react'
import { Button } from 'ui/src/components/Button/Button'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { LargeDialog } from 'ui/src/components/Dialog/LargeDialog'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { visuallyHidden } from 'ui/src/utils/visibility.css'
import { HedvigSavLogo } from './HedvigSavLogo'
import { vintageShopLargeDialogContent } from '../vintageShopLargeDialog.css'
import { partnerFooter, partnerLogo } from './VintageShopFooter.css'

export type VintageShopFooterProps = {
  readMoreDialog?: Array<SbBlokData>
  showAllProductsLink?: boolean
}

export function VintageShopFooter({ readMoreDialog, showAllProductsLink }: VintageShopFooterProps) {
  const { t } = useTranslation(['vintageShop', 'common'])
  const locale = useRoutingLocale()

  return (
    <GridLayout.Root>
      <GridLayout.Content width={{ _: '1', md: '5/6', lg: '2/3', xl: '1/2' }} align="center">
        <section className={partnerFooter}>
          <HedvigSavLogo className={partnerLogo} />
          <div className={yStack({ gap: { _: 'lg', lg: 'xl' }, alignItems: 'center', paddingInline: 'md' })}>
            <Text align="center" balance={true}>
              {t('VINTAGE_SHOP_FOOTER_HEADING')}
            </Text>
            <div className={yStack({ gap: 'sm', justifyContent: 'center' })}>
              {readMoreDialog?.length ? (
                <LargeDialog.Root>
                  <Dialog.Trigger asChild>
                    <Button variant="primary">{t('VINTAGE_SHOP_READ_MORE_BUTTON')}</Button>
                  </Dialog.Trigger>
                  <LargeDialog.Content className={vintageShopLargeDialogContent}>
                    <LargeDialog.CloseIconButton aria-label={t('common:DIALOG_CLOSE')} />
                    <Dialog.Title className={visuallyHidden}>{t('VINTAGE_SHOP_READ_MORE_DIALOG_TITLE')}</Dialog.Title>
                    <div className={sprinkles({ marginTop: 'sm' })}>
                      {readMoreDialog.map((item) => (
                        <StoryblokComponent blok={item} key={item._uid} nested={true} />
                      ))}
                    </div>
                  </LargeDialog.Content>
                </LargeDialog.Root>
              ) : null}
              {showAllProductsLink && (
                <TextLink href={PageLink.vintageShop({ locale }).pathname}>
                  {t('VINTAGE_SHOP_SHOW_ALL_PRODUCTS_LINK')}
                </TextLink>
              )}
            </div>
          </div>
        </section>
      </GridLayout.Content>
    </GridLayout.Root>
  )
}
